/**
 * LandingPage — public root for unauthenticated visitors on a fresh device.
 * No waitlist count. "Try it free" dominant + Sign up / Sign in.
 * Returning variant adds "Not you? Pick a profile" link.
 *
 * Legal footer (launch-compliance R6.2, R9): the four Legal Center docs
 * must be reachable from the public landing BEFORE signup, so a visitor
 * can read what we collect before agreeing to anything. Self-mounts its
 * own LegalModal so the surface stays leaf-component-clean.
 *
 * The kidSAFE seal sits above the legal links on the web, and only on a device
 * we don't recognize: a first visit is where a grown-up decides whether to
 * trust the app, while a recognized family device is a doorway kids pass
 * through (operator, 2026-10-24). Never in the iPad app.
 */
import { useState } from 'react';
import { ChromeTokens } from '../chrome/tokens';
import { Icon } from '../chrome/Icon';
import { LegalModal } from '../legal/LegalModal';
import { KidSafeSeal } from '../legal/KidSafeSeal';
import { isNativePlatform } from '../platform';
import type { LegalDocId } from '../legal/docs';

interface LandingPageProps {
  onTryIt: () => void;
  onSignup: () => void;
  onLogin: () => void;
  /** If true, shows "Not you? Pick a profile" link */
  returning?: boolean;
  onProfiles?: () => void;
}

/**
 * Text-styled buttons that still have to be tappable by a small hand.
 * `--touch-target-min` is 44px (Apple HIG floor); see chrome/global.css.
 */
const landingLinkBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: ChromeTokens.color.ink,
  cursor: 'pointer',
  fontSize: 15,
  fontWeight: 600,
  fontFamily: ChromeTokens.font.ui,
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 'var(--touch-target-min)',
  padding: '0 4px',
};

export function LandingPage({ onTryIt, onSignup, onLogin, returning, onProfiles }: LandingPageProps) {
  const [legalDoc, setLegalDoc] = useState<LegalDocId | null>(null);
  return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(180deg, ${ChromeTokens.color.sky}, ${ChromeTokens.color.base})`,
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      // Pin the legal footer to the bottom while the hero stays optically centered.
      justifyContent: 'space-between',
      // The xl gutter, or a phone's safe area where that is bigger (the
      // legal footer sits on the bottom edge, over the home indicator).
      padding: `max(${ChromeTokens.spacing.xl}px, var(--safe-top)) max(${ChromeTokens.spacing.xl}px, var(--safe-right)) ` +
        `max(${ChromeTokens.spacing.xl}px, var(--safe-bottom)) max(${ChromeTokens.spacing.xl}px, var(--safe-left))`,
      boxSizing: 'border-box', gap: 24,
    }}>
      {/* Spacer so the hero block centers visually without the footer pulling it. */}
      <div aria-hidden="true" />
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24 }}>
      {/* Logo lockup */}
      <img
        src="/logo/wordmark-color-medium.png"
        alt="world by me"
        style={{ height: 52, width: 'auto', display: 'block' }}
      />

      <p style={{ color: ChromeTokens.color.inkMuted, fontSize: 17, textAlign: 'center', maxWidth: 360, margin: 0, lineHeight: 1.5 }}>
        Speak to build games, animations, stories, songs and little apps.
      </p>

      {/* Primary CTA */}
      <button
        onClick={onTryIt}
        aria-label="Try it free"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 10,
          padding: '14px 32px', minHeight: 52,
          borderRadius: ChromeTokens.radius.pill, border: 'none', cursor: 'pointer',
          background: ChromeTokens.color.sun, color: ChromeTokens.color.ink,
          fontWeight: 700, fontSize: 17, fontFamily: ChromeTokens.font.ui,
          boxShadow: ChromeTokens.shadow.soft,
        }}
      >
        <Icon name="sparkles" size={20} /> Try it free
      </button>

      {/* Secondary links.
          `minHeight: var(--touch-target-min)` grows the TAP target to Apple's
          44pt floor without touching the type size — these measured 25px tall
          on an iPad viewport, roughly half the minimum, and a 6-year-old's tap
          is both larger and less precise than an adult's
          (ipad-native-shell R5.6). `inline-flex` + centered items keeps the
          label optically where it was. */}
      <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
        <button onClick={onSignup} style={{ ...landingLinkBtn }}>
          Sign up
        </button>
        <span style={{ color: ChromeTokens.color.baseMuted }}>·</span>
        <button onClick={onLogin} style={{ ...landingLinkBtn }}>
          Sign in
        </button>
      </div>

      {/* Returning variant */}
      {returning && onProfiles && (
        <button onClick={onProfiles} style={{ background: 'none', border: 'none', color: ChromeTokens.color.inkMuted, cursor: 'pointer', fontSize: 13, marginTop: 8, minHeight: 'var(--touch-target-min)' }}>
          Not you? Pick a profile
        </button>
      )}
      </div>

      {/* Legal footer (R6.2) — Privacy / Safety / Terms / Support reachable
          before signup. Address block per R6.9. */}
      <footer style={{
        width: '100%', maxWidth: 720, marginTop: 32,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
        fontFamily: ChromeTokens.font.ui,
      }}>
        {!returning && !isNativePlatform() && <KidSafeSeal />}
        <nav aria-label="Legal" style={{
          display: 'flex', flexWrap: 'wrap', gap: 18, justifyContent: 'center',
        }}>
          {([
            { id: 'privacy', label: 'Privacy' },
            { id: 'safety', label: 'Safety & COPPA' },
            { id: 'terms', label: 'Terms' },
            { id: 'support', label: 'Support' },
          ] as const).map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => setLegalDoc(d.id)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: ChromeTokens.color.inkMuted, fontSize: 13, fontWeight: 600,
                minHeight: 'var(--touch-target-min)',
              }}
            >
              {d.label}
            </button>
          ))}
        </nav>
        <address style={{
          fontStyle: 'normal', textAlign: 'center', fontSize: 12,
          color: ChromeTokens.color.inkMuted, lineHeight: 1.5,
        }}>
          world by me Inc.<br />
          202 Walton Way Ste 192 Unit 744<br />
          Cedar Park, TX 78613
        </address>
      </footer>

      <LegalModal
        open={legalDoc !== null}
        doc={legalDoc ?? 'privacy'}
        onClose={() => setLegalDoc(null)}
      />
    </div>
  );
}
